import type { IntlShape } from 'react-intl';

import { ErrorKeySet, type Validator } from '../../../types/field-validation';
import type { FormFieldValidation } from '../../../types/form/form-fields';
import type { NumberValue } from '../../../types/form/form-fields/field-values';
import { getFailedRequiredValidationError, getFailedValidationError } from '../utils';
import { messages } from './messages';

export const getNumberValidationErrors = (
	validation: FormFieldValidation,
	fieldLabel: string,
	intl?: IntlShape,
	numberValue?: NumberValue,
): ReturnType<Validator> => {
	if (Object.keys(validation).length === 0) {
		return [];
	}

	if (!numberValue) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}

	const { number } = numberValue;

	if (validation.wholeNumbersOnly && !Number.isInteger(number)) {
		return [
			getFailedValidationError(ErrorKeySet.INVALID, intl?.formatMessage(messages.wholeNumbersOnly)),
		];
	}

	// Check equal
	if (
		validation.minimumNumber &&
		validation.minimumNumber === validation.maximumNumber &&
		number !== validation.minimumNumber
	) {
		return [
			getFailedValidationError(
				ErrorKeySet.INVALID,
				intl?.formatMessage(messages.exactNumber, { number: validation.minimumNumber }),
			),
		];
	}

	// check between
	if (
		validation.minimumNumber &&
		validation.maximumNumber &&
		(number < validation.minimumNumber || number > validation.maximumNumber)
	) {
		return [
			getFailedValidationError(
				ErrorKeySet.INVALID,
				intl?.formatMessage(messages.betweenNumber, {
					minimum: validation.minimumNumber,
					maximum: validation.maximumNumber,
				}),
			),
		];
	}

	if (validation.minimumNumber && number < validation.minimumNumber) {
		return [
			getFailedValidationError(
				ErrorKeySet.INVALID,
				intl?.formatMessage(messages.minimumNumber, { minimum: validation.minimumNumber }),
			),
		];
	}

	if (validation.maximumNumber && number > validation.maximumNumber) {
		return [
			getFailedValidationError(
				ErrorKeySet.INVALID,
				intl?.formatMessage(messages.maximumNumber, { maximum: validation.maximumNumber }),
			),
		];
	}
	return [];
};
